import React, { Fragment, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { SegmentedButton, Flexer, Field } from '@ontruck/orc';
import Select from 'components/Select';

import {
  LOAD_TYPE_SWITCH_FIELD,
  LOAD_TYPE_FIELD,
  PALLET_TYPE_FIELD,
  ITEM_WIDTH_FIELD,
  ITEM_LENGTH_FIELD,
  ITEM_HEIGHT_FIELD,
  LOAD_TYPE_SWITCH,
  LOAD_TYPES,
  PALLET_TYPES,
  NON_PALLETIZED_TYPES,
  PALLET_SIZES,
  NON_PALLETIZED_SIZES,
} from 'modules/shipper-order/Form/constants';

import { withFormikField, getFormikError } from 'utils/formik';

const t = {
  width: 'Width',
  length: 'Length',
  height: 'Height',
  cm: 'cm',
  load_type_switch: {
    PALLET: 'Palletized',
    NON_PALLETIZED: 'Non-palletized',
  },
  load_type: {
    PALLET: 'Pallet',
    PARCEL: 'Parcel',
    BULK: 'Bulk',
  },
  types: {
    EU_PALLET: 'European',
    US_PALLET: 'American',
    CUSTOM_PALLET: 'Custom',
    PARCEL: 'Parcel',
    BULK: 'Bulk',
  },
};

const LoadTypeSelector = ({ field, form }) => {
  const { value = {} } = field;
  const { pallet_type, item_width, item_length, load_type, item_height, load_type_switch } = value;
  const radioButton =
    load_type === LOAD_TYPES.PALLET ? LOAD_TYPE_SWITCH.PALLET : LOAD_TYPE_SWITCH.NON_PALLETIZED;
  const [loadTypeSwitch, setLoadTypeSwitch] = useState(radioButton);
  const [loadType, setLoadType] = useState(load_type);
  const [palletType, setPalletType] = useState(pallet_type);
  const { setFieldValue } = form;
  const isDimensionsDisabled =
    pallet_type !== PALLET_TYPES.CUSTOM_PALLET && load_type === LOAD_TYPES.PALLET;
  const setSizeFields = useCallback(
    (load) => {
      setFieldValue(`${field.name}.${ITEM_WIDTH_FIELD}`, load?.item_width);
      setFieldValue(`${field.name}.${ITEM_LENGTH_FIELD}`, load?.item_length);
    },
    [setFieldValue, field]
  );
  const onPalletChange = useCallback(
    ({ value }) => {
      setFieldValue(`${field.name}.${LOAD_TYPE_FIELD}`, LOAD_TYPES.PALLET);
      setFieldValue(`${field.name}.${PALLET_TYPE_FIELD}`, value);
      setSizeFields(PALLET_SIZES[value]);
      setPalletType(value);
    },
    [setFieldValue, field, setSizeFields]
  );
  const onNonPalletizedChange = useCallback(
    ({ value }) => {
      setFieldValue(`${field.name}.${LOAD_TYPE_FIELD}`, LOAD_TYPES[value]);
      setFieldValue(`${field.name}.${PALLET_TYPE_FIELD}`, PALLET_TYPES.CUSTOM_PALLET);
      setSizeFields(NON_PALLETIZED_SIZES[value]);
    },
    [setFieldValue, field, setSizeFields]
  );

  useEffect(() => {
    if (!load_type_switch || load_type_switch === loadTypeSwitch) {
      return;
    }

    if (load_type_switch === LOAD_TYPE_SWITCH.PALLET) {
      const value = palletType || PALLET_TYPES.EU_PALLET;
      onPalletChange({ value });
    } else if (load_type_switch === LOAD_TYPE_SWITCH.NON_PALLETIZED) {
      const validLoad = loadType && loadType !== LOAD_TYPES.PALLET;
      const value = validLoad ? loadType : LOAD_TYPES.PARCEL;
      onNonPalletizedChange({ value });
      setLoadType(load_type);
    }
    setLoadTypeSwitch(load_type_switch);
  }, [
    setFieldValue,
    field,
    load_type_switch,
    loadTypeSwitch,
    load_type,
    loadType,
    palletType,
    onNonPalletizedChange,
    onPalletChange,
    setLoadTypeSwitch,
    setLoadType,
  ]);

  return (
    <Flexer.Parent>
      <Flexer.Child>
        <SegmentedButton.Parent
          size="M"
          value={loadTypeSwitch}
          onChange={(e, { value }) => {
            setFieldValue(`${field.name}.${LOAD_TYPE_SWITCH_FIELD}`, value);
          }}
        >
          <SegmentedButton.Child
            value={LOAD_TYPE_SWITCH.PALLET}
            label={t.load_type_switch.PALLET}
          />
          <SegmentedButton.Child
            value={LOAD_TYPE_SWITCH.NON_PALLETIZED}
            label={t.load_type_switch.NON_PALLETIZED}
          />
        </SegmentedButton.Parent>
      </Flexer.Child>
      <Flexer.Child mLeft="S">
        {loadTypeSwitch === LOAD_TYPE_SWITCH.PALLET && (
          <Select
            placeholder={pallet_type}
            ignoreCase={true}
            matchPos="any"
            clearable={false}
            small
            value={pallet_type}
            searchable={false}
            options={Object.keys(PALLET_TYPES).map((type) => ({
              value: PALLET_TYPES[type],
              label: t.types[PALLET_TYPES[type]],
            }))}
            onChange={onPalletChange}
            style={{ width: 150 }}
          />
        )}
        {loadTypeSwitch === LOAD_TYPE_SWITCH.NON_PALLETIZED && (
          <Select
            placeholder={load_type}
            ignoreCase={true}
            matchPos="any"
            clearable={false}
            small
            value={load_type}
            searchable={false}
            options={Object.keys(NON_PALLETIZED_TYPES).map((type) => ({
              value: NON_PALLETIZED_TYPES[type],
              label: t.types[NON_PALLETIZED_TYPES[type]],
            }))}
            onChange={onNonPalletizedChange}
            style={{ width: 150 }}
          />
        )}
      </Flexer.Child>
      <Fragment>
        <Flexer.Child mLeft="S" mRight="S">
          <Field
            label={t.width}
            value={item_width}
            width="S"
            asideRightText={t.cm}
            onChange={(e, { value }) => setFieldValue(`${field.name}.${ITEM_WIDTH_FIELD}`, value)}
            invalid={
              !!getFormikError({ field: { name: `${field.name}.${ITEM_WIDTH_FIELD}` }, form })
            }
            disabled={isDimensionsDisabled}
          />
        </Flexer.Child>
        <Flexer.Child>
          <Field
            label={t.length}
            value={item_length}
            width="S"
            asideRightText={t.cm}
            onChange={(e, { value }) => setFieldValue(`${field.name}.${ITEM_LENGTH_FIELD}`, value)}
            invalid={
              !!getFormikError({ field: { name: `${field.name}.${ITEM_LENGTH_FIELD}` }, form })
            }
            disabled={isDimensionsDisabled}
          />
        </Flexer.Child>
        <Flexer.Child mLeft="S">
          <Field
            label={t.height}
            value={item_height}
            width="S"
            asideRightText={t.cm}
            onChange={(e, { value }) => setFieldValue(`${field.name}.${ITEM_HEIGHT_FIELD}`, value)}
            invalid={
              !!getFormikError({ field: { name: `${field.name}.${ITEM_HEIGHT_FIELD}` }, form })
            }
          />
        </Flexer.Child>
      </Fragment>
    </Flexer.Parent>
  );
};

LoadTypeSelector.propTypes = {
  field: PropTypes.object,
  form: PropTypes.object,
};

export default withFormikField(LoadTypeSelector);
